<template>
  <div>
    <!-- App Bar -->
    <v-app-bar :class="['app-header', { 'app-header-light': store.theme === 'light' }]" elevation="0" height="64">
      <!-- Mobile menu toggle (only when logged in) -->
      <v-app-bar-nav-icon v-if="store.loggedIn" class="d-md-none nav-toggle" @click="drawer = !drawer" />

      <!-- Logo -->
      <v-app-bar-title class="logo-title">
        <img :src="'/' + store.fslabsLogo" alt="FSLabs" height="32" class="logo-img" />
      </v-app-bar-title>

      <!-- Desktop Navigation Links (only when logged in) -->
      <nav v-if="store.loggedIn" class="d-none d-md-flex nav-links">
        <v-btn
          to="/"
          :ripple="false"
          variant="text"
          class="nav-btn"
          prepend-icon="mdi-home"
        >
          Home
        </v-btn>
        <v-btn
          v-for="(item, i) in navItems"
          :key="i"
          :to="item.action ? undefined : item.route"
          :href="item.href || undefined"
          :target="item.href ? '_blank' : undefined"
          :ripple="false"
          variant="text"
          :class="['nav-btn', { 'nav-btn-active': item.route && route.path.startsWith(item.route) }]"
          :prepend-icon="item.icon"
          @click="item.action ? item.action() : undefined"
        >
          {{ item.title }}
        </v-btn>
      </nav>

      <v-spacer />

      <!-- Theme Toggle Component -->
      <ThemeToggle @theme-changed="handleThemeChange" />

      <!-- Login Button -->
      <div class="login-wrapper">
        <login-button />
      </div>
    </v-app-bar>

    <!-- Mobile Navigation Drawer -->
    <v-navigation-drawer v-model="drawer" temporary location="left" class="mobile-drawer">
      <div class="drawer-header">
        <img :src="'/' + store.fslabsLogo" alt="FSLabs" height="28" />
      </div>
      <v-divider />
      <v-list nav class="drawer-list">
        <v-list-item
          to="/"
          prepend-icon="mdi-home"
          class="drawer-item"
        >
          <v-list-item-title>Home</v-list-item-title>
        </v-list-item>
        <v-list-item
          v-for="(item, i) in navItems"
          :key="i"
          :to="item.action ? undefined : item.route"
          :href="item.href || undefined"
          :target="item.href ? '_blank' : undefined"
          :prepend-icon="item.icon"
          class="drawer-item"
          @click="item.action ? item.action() : undefined"
        >
          <v-list-item-title>{{ item.title }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="3000" location="bottom">
      {{ snackbarText }}
      <template v-slot:actions>
        <v-btn variant="text" icon="mdi-close" @click="showSnackbar = false" size="small" />
      </template>
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount, computed } from "vue";
import LoginButton from "./LoginButton.vue";
import ThemeToggle from "./ThemeToggle.vue";
import { useStore } from "../store/store";
import router from "../router";
import { useRoute } from "vue-router";
import { useTheme } from "vuetify";

const store = useStore();
const vuetifyTheme = useTheme();
const route = useRoute();
const drawer = ref(false);

// Snackbar state
const showSnackbar = ref(false);
const snackbarText = ref("");
const snackbarColor = ref("success");

// Navigation items
const navItems = computed(() => [
  {
    title: "Search",
    icon: "mdi-magnify",
    route: "/crates"
  },
  {
    title: "Settings",
    icon: "mdi-cog",
    route: "/settings",
    action: goToSettings
  },
  {
    title: "Doc Queue",
    icon: "mdi-layers",
    route: "/docqueue"
  },
  {
    title: "API Docs",
    icon: "mdi-api",
    href: "/api/docs"
  }
]);

onBeforeMount(() => {
  // Apply the current theme
  vuetifyTheme.global.name.value = store.theme;
  setTheme(store.theme);
});

function goToSettings() {
  if (store.loggedIn === false) {
    router.push({ path: "/login", query: { redirect: "settings" } });
  } else {
    router.push("/settings");
  }
}

function handleThemeChange() {
  // Show notification for theme change
  showNotification(`Switched to ${store.theme} theme`);
}

function setTheme(theme: string) {
  let body = document.getElementById("body");
  body?.setAttribute("color-theme", theme);
}

// Show notification snackbar
function showNotification(message: string, isError: boolean = false) {
  snackbarText.value = message;
  snackbarColor.value = isError ? "error" : "success";
  showSnackbar.value = true;
}
</script>

<style scoped>
.app-header {
  background: rgba(14, 14, 16, 0.75) !important;
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border-bottom: 1px solid rgba(50, 50, 56, 0.25);
}

/* Light theme: more transparent to show gradient background */
.app-header-light {
  background: rgba(240, 240, 242, 0.8) !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
}

.nav-toggle {
  color: rgb(var(--v-theme-on-surface));
}

.logo-title {
  flex: none;
  width: auto;
  min-width: auto;
}

.logo-img {
  display: block;
}

.nav-links {
  margin-left: 24px;
  gap: 4px;
}

.nav-btn {
  font-weight: 500;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgb(var(--v-theme-on-surface));
  padding: 0 16px;
  height: 40px;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.nav-btn:hover {
  background: rgba(var(--v-theme-primary), 0.08);
  color: rgb(var(--v-theme-primary));
}

.nav-btn.nav-btn-active,
.nav-btn.v-btn--active {
  background: rgba(var(--v-theme-primary), 0.12) !important;
  color: rgb(var(--v-theme-primary)) !important;
}

.nav-btn :deep(.v-icon) {
  font-size: 18px;
  opacity: 0.8;
}

.nav-btn:hover :deep(.v-icon),
.nav-btn.nav-btn-active :deep(.v-icon),
.nav-btn.v-btn--active :deep(.v-icon) {
  opacity: 1;
}

.login-wrapper {
  padding-right: 16px;
}

/* Mobile Drawer */
.mobile-drawer {
  background: rgba(20, 20, 22, 0.95) !important;
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
}

.drawer-header {
  padding: 20px 16px;
  background: rgba(var(--v-theme-primary), 0.05);
}

.drawer-list {
  padding: 8px;
}

.drawer-item {
  border-radius: 8px;
  margin-bottom: 4px;
}

.drawer-item:hover {
  background: rgba(var(--v-theme-primary), 0.08);
}

.drawer-item :deep(.v-list-item__prepend .v-icon) {
  color: rgb(var(--v-theme-primary));
  opacity: 0.8;
}
</style>
